import { React, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import PasswordInput from "../../components/ui/password-input";
import Loader from "../../components/ui/loader";
import { FcGoogle } from "react-icons/fc";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { FaCheck } from "react-icons/fa6";
import Footer from "../../components/navigation/footer";

// Copy shown above the fields on each step of the wizard
const STEP_COPY = [
    {
        eyebrow: "Step 1 of 4",
        title: "Let's start with your name",
        subtitle:
            "We'll use this to personalize your account and how you appear on the platform.",
    },
    {
        eyebrow: "Step 2 of 4",
        title: "What do you do?",
        subtitle:
            "Your occupation helps us tailor course recommendations to your background and financial literacy level.",
    },
    {
        eyebrow: "Step 3 of 4",
        title: "How can we reach you?",
        subtitle:
            "We'll use this number for communication and important updates about your learning.",
    },
    {
        eyebrow: "Step 4 of 4",
        title: "Set up your login",
        subtitle:
            "Enter your preferred email address and password to secure your account. You may also review our terms here as well.",
    },
];

const TOTAL_STEPS = STEP_COPY.length;

function Register() {
    const API_URL = process.env.REACT_APP_API_URL;
    const [message, setMessage] = useState({ text: "", type: "", visible: false });
    const navigate = useNavigate();
    const [instructorAllowed, setInstructorAllowed] = useState(false);
    const capitalizeFirst = (str) => (str ? str.charAt(0).toUpperCase() + str.slice(1) : "");
    const [isLoading, setIsLoading] = useState(false);
    const [passwordVisible, setPasswordVisible] = useState(false);
    const [confirmPasswordVisible, setConfirmPasswordVisible] = useState(false);
    const [isTermsAccepted, setIsTermsAccepted] = useState(false);
    const isMaintenance = process.env.REACT_APP_MAINTENANCE_MODE;

    // True once we've confirmed there's a pending Google registration to
    // resume — locks the name/email/password fields and skips the
    // credential step's validation, since Google owns those details.
    const [isGoogleFlow, setIsGoogleFlow] = useState(false);
    const [isResumingGoogle, setIsResumingGoogle] = useState(false);

    // Which step of the wizard we're on: 0 = name, 1 = occupation, 2 = phone, 3 = credentials
    const [step, setStep] = useState(0);

    const [formData, setFormData] = useState({
        firstName: "",
        lastName: "",
        email: "",
        password: "",
        confirmPassword: "",
        role: "Learner",
        occupation: "",
        phone: "",
    });

    const togglePasswordVisibility = () => {
        setPasswordVisible(!passwordVisible);
    };

    const toggleConfirmPasswordVisibility = () => {
        setConfirmPasswordVisible(!confirmPasswordVisible);
    };

    const showMessage = (text, type) => {
        setMessage({ text, type, visible: true });
        setTimeout(() => {
            setMessage((prev) => ({ ...prev, visible: false }));
        }, 5000);
    };

    const getDomain = (email = "") => {
        const at = email.indexOf("@");
        if (at === -1) return "";
        return email.slice(at + 1).toLowerCase();
    };

    // Update whether Instructor should be selectable whenever email changes
    useEffect(() => {
        const domain = getDomain(formData.email);
        const allowed = domain === "luse.co.zm";
        setInstructorAllowed(allowed);

        // If currently role is Instructor but domain is no longer allowed, force to Learner
        if (!allowed && formData.role === "Instructor") {
            setFormData((prev) => ({ ...prev, role: "Learner" }));
        }
    }, [formData.email]);

    // Handle input changes
    const handleChange = (e) => {
        const { name, value } = e.target;
        let updatedValue = value;

        if (name === "firstName" || name === "lastName") {
            updatedValue = value.replace(/[^a-zA-Z]/g, ""); // Allows only letters
            updatedValue = updatedValue.charAt(0).toUpperCase() + updatedValue.slice(1).toLowerCase();
        }

        if (name === "role") {
            if (value === "Instructor" && !instructorAllowed) {
                showMessage("You must use an luse.co.zm email to register as an Instructor.", "error");
                return;
            }
            setFormData((prev) => ({ ...prev, role: value }));
            return;
        }

        setFormData((prev) => ({ ...prev, [name]: updatedValue }));
    };

    // Function to check if password meets the strength criteria
    const isPasswordStrong = (password) => {
        const strongPasswordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+={};':",.<>?/|[\]]).{8,}$/;
        return strongPasswordRegex.test(password);
    };

    const params = new URLSearchParams(window.location.search);

    // Check for Google OAuth error on mount if user already exists
    const error = params.get("error");
    useEffect(() => {
        if (error) {
            showMessage(error, "error");
        }
    }, [error]);

    // If we're returning from Google with ?googleSetup=1, there's a pending
    // registration waiting server-side (email/first/last name only — no
    // account has been created yet). Fetch those details, lock them into
    // the form, and jump straight to the occupation step.
    useEffect(() => {
        const googleSetup = params.get("googleSetup");
        if (googleSetup !== "1") return;

        setIsResumingGoogle(true);

        fetch(`${API_URL}/auth/google/pending`, { credentials: "include" })
            .then((res) => {
                if (!res.ok) throw new Error("no-pending-registration");
                return res.json();
            })
            .then((data) => {
                setFormData((prev) => ({
                    ...prev,
                    firstName: data.firstName || "",
                    lastName: data.lastName || "",
                    email: data.email || "",
                }));
                setIsGoogleFlow(true);
                setStep(1);
            })
            .catch(() => {
                showMessage(
                    "We couldn't find your Google sign-in details — please try continuing with Google again.",
                    "error"
                );
            })
            .finally(() => setIsResumingGoogle(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Per-step validation — returns an error string, or "" if the step is good to go
    const validateStep = (currentStep) => {
        const { firstName, lastName, occupation, phone, email, password, confirmPassword, role } = formData;

        switch (currentStep) {
            case 0:
                if (!firstName.trim() || !lastName.trim()) {
                    return "Let's get your first and last name down before we move on.";
                }
                return "";
            case 1:
                if (!occupation.trim()) {
                    return "Tell us what you do for a living — even one word works!";
                }
                return "";
            case 2:
                if (!phone || phone.trim().length < 6) {
                    return "We'll need a valid phone number to keep this account.";
                }
                return "";
            case 3:
                if (isGoogleFlow) {
                    // Email, password, and identity are managed by Google — just role and terms left to check.
                    if (!role) return "Please choose whether you're a Learner or an Instructor.";
                    if (!isTermsAccepted) return "Please accept the Terms of Service to finish signing up.";
                    return "";
                }
                if (!email.trim()) return "An email address is required to finish signing up.";
                if (!role) return "Please choose whether you're a Learner or an Instructor.";
                if (!isPasswordStrong(password)) {
                    return "Password must be at least 8 characters long and include an uppercase letter, a lowercase letter, a number, and a special character.";
                }
                if (password !== confirmPassword) return "Passwords do not match.";
                if (!isTermsAccepted) return "Please accept the Terms of Service to finish signing up.";
                return "";
            default:
                return "";
        }
    };

    const goNext = () => {
        const validationError = validateStep(step);
        if (validationError) {
            showMessage(validationError, "error");
            return;
        }
        setStep((prev) => Math.min(prev + 1, TOTAL_STEPS - 1));
    };

    const goBack = () => {
        setStep((prev) => Math.max(prev - 1, 0));
    };

    const handleGoogleClick = () => {
        window.location.href = `${API_URL}/auth/google/register`;
    };

    // Handle form submission (final step only)
    const handleSubmit = async (e) => {
        e.preventDefault();

        const validationError = validateStep(3);
        if (validationError) {
            showMessage(validationError, "error");
            return;
        }

        setIsLoading(true);

        const endpoint = isGoogleFlow ? `${API_URL}/auth/google/complete-registration` : `${API_URL}/register`;

        const payload = isGoogleFlow
            ? {
                  occupation: formData.occupation,
                  phone: formData.phone,
                  role: formData.role,
              }
            : {
                  firstName: formData.firstName,
                  lastName: formData.lastName,
                  email: formData.email,
                  password: formData.password,
                  role: formData.role,
                  occupation: formData.occupation,
                  phone: formData.phone,
              };

        try {
            const response = await fetch(endpoint, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                credentials: "include",
                body: JSON.stringify(payload),
            });

            const result = await response.json();

            if (response.ok) {
                showMessage(result.message, "success");
                setTimeout(() => {
                    navigate("/login");
                }, 7500);

                setFormData({
                    firstName: "",
                    lastName: "",
                    email: "",
                    password: "",
                    confirmPassword: "",
                    role: "Learner",
                    occupation: "",
                    phone: "",
                });
                setIsLoading(false);
            } else {
                setIsLoading(false);
                showMessage(result.message || "Error Registering", "error");
            }
        } catch (err) {
            setIsLoading(false);
            console.error("Error:", err);
            showMessage("Something went wrong.", "error");
        }
    };

    const { eyebrow, title, subtitle } = STEP_COPY[step];

    return (
        <>
            <div className="register-container">
                {/* alert */}
                {message.text && (
                    <div
                        className={`toast-alert ${message.visible ? "toast-show" : ""} ${
                            message.type === "success" ? "toast-success" : "toast-error"
                        }`}
                        role="alert"
                    >
                        {message.text}
                    </div>
                )}

                {isLoading || isResumingGoogle ? (
                    <Loader />
                ) : (
                    <main className="form-signup container p-4 rounded">
                        <div className="register-logo-heading">
                            <img className="mb-4" src="/images/luse_logo.png" alt="" width="60" height="60" />
                            <h1 className="h3 mb-3 display-10 fw-bold">Register</h1>
                        </div>

                        {/* step progress indicator */}
                        <div className="register-step-progress d-flex align-items-center justify-content-between mb-4">
                            {STEP_COPY.map((s, i) => (
                                <div className="register-step-dot-wrapper" key={s.eyebrow}>
                                    <div
                                        className={`register-step-dot ${i === step ? "register-step-dot-active" : ""} ${
                                            i < step ? "completed" : ""
                                        }`}
                                    >
                                        {i < step ? <FaCheck /> : i + 1}
                                    </div>
                                    {i < STEP_COPY.length - 1 && (
                                        <div className={`register-step-line ${i < step ? "" : ""}`} />
                                    )}
                                </div>
                            ))}
                        </div>

                        {/* step heading */}
                        <div className="register-step-heading mb-4">
                            <span className="register-step-eyebrow" style={{ color: "#ffa103", fontWeight: 600 }}>
                                {eyebrow}
                            </span>
                            <h4 className="fw-bold mt-1 mb-2">{title}</h4>
                            <p className="text-body-secondary mb-0">{subtitle}</p>
                            {isGoogleFlow && (
                                <p className="mb-0 mt-2" style={{ fontSize: "0.9rem", color: "#039606" }}>
                                    Signed in with Google as {formData.firstName} {formData.lastName} ({formData.email})
                                </p>
                            )}
                        </div>

                        <form onSubmit={handleSubmit}>
                            {/* STEP 0: Name */}
                            {step === 0 && (
                                <>
                                    <div className="row g-3">
                                        <div className="col-md-6">
                                            <div className="form-floating">
                                                <input
                                                    name="firstName"
                                                    type="text"
                                                    className="form-control"
                                                    id="firstName"
                                                    placeholder="First Name"
                                                    value={capitalizeFirst(formData.firstName)}
                                                    onChange={handleChange}
                                                    autoComplete="given-name"
                                                    disabled={isGoogleFlow}
                                                    required
                                                />
                                                <label htmlFor="firstName">First Name</label>
                                            </div>
                                        </div>

                                        <div className="col-md-6">
                                            <div className="form-floating">
                                                <input
                                                    name="lastName"
                                                    type="text"
                                                    className="form-control"
                                                    id="lastName"
                                                    placeholder="Last Name"
                                                    value={capitalizeFirst(formData.lastName)}
                                                    onChange={handleChange}
                                                    autoComplete="family-name"
                                                    disabled={isGoogleFlow}
                                                    required
                                                />
                                                <label htmlFor="lastName">Last Name</label>
                                            </div>
                                        </div>
                                    </div>

                                    {!isGoogleFlow && isMaintenance !== "true" && (
                                        <div className="mt-4">
                                            <div className="or mb-3 text-center">
                                                <p className="fw-normal text-body-emphasis">OR</p>
                                            </div>
                                            <button
                                                type="button"
                                                onClick={handleGoogleClick}
                                                className="btn secondary-btn w-100 py-2"
                                            >
                                                <FcGoogle style={{ width: "20px", height: "20px" }} className="me-2" />
                                                Continue with Google
                                            </button>
                                        </div>
                                    )}
                                </>
                            )}

                            {/* STEP 1: Occupation */}
                            {step === 1 && (
                                <div className="row g-3">
                                    <div className="col-12">
                                        <div className="form-floating">
                                            <input
                                                name="occupation"
                                                type="text"
                                                className="form-control"
                                                id="occupation"
                                                placeholder="Occupation"
                                                value={capitalizeFirst(formData.occupation)}
                                                onChange={handleChange}
                                                required
                                            />
                                            <label htmlFor="occupation">Occupation</label>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* STEP 2: Phone */}
                            {step === 2 && (
                                <div className="row g-3">
                                    <div className="col-12">
                                        <div className="form-floating">
                                            <PhoneInput
                                                country={"zm"}
                                                value={formData.phone}
                                                onChange={(phone) => setFormData((prev) => ({ ...prev, phone }))}
                                                inputClass="form-control"
                                                buttonClass="phone-flag-button"
                                                inputProps={{
                                                    name: "phone",
                                                    required: true,
                                                }}
                                            />
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* STEP 3: Credentials */}
                            {step === 3 && (
                                <>
                                    <div className="form-floating my-3">
                                        <input
                                            name="email"
                                            type="email"
                                            className="form-control"
                                            id="emailAddress"
                                            placeholder="name@example.com"
                                            value={formData.email}
                                            onChange={handleChange}
                                            disabled={isGoogleFlow}
                                            required
                                        />
                                        <label htmlFor="emailAddress">Email Address</label>
                                    </div>

                                    {isGoogleFlow ? (
                                        <p className="text-body-secondary" style={{ fontSize: "0.9rem" }}>
                                            Your password is managed by Google — there's nothing to set here.
                                        </p>
                                    ) : (
                                        <>
                                            <PasswordInput
                                                name="password"
                                                value={formData.password}
                                                onChange={handleChange}
                                                label="Password"
                                                required
                                            />

                                            <PasswordInput
                                                name="confirmPassword"
                                                value={formData.confirmPassword}
                                                onChange={handleChange}
                                                label="Confirm Password"
                                                required
                                            />
                                        </>
                                    )}

                                    {/* role */}
                                    <div className="">
                                        <div className="pt-3">
                                            <h6>Register as:</h6>
                                        </div>
                                        <div className="d-flex gap-3">
                                            <div className="form-check text-start my-3">
                                                <input
                                                    className="form-check-input"
                                                    type="radio"
                                                    name="role"
                                                    value="Learner"
                                                    id="learner"
                                                    onChange={handleChange}
                                                    checked={formData.role === "Learner"}
                                                />
                                                <label className="form-check-label" htmlFor="learner">
                                                    Learner
                                                </label>
                                            </div>
                                            <div className="form-check text-start my-3">
                                                <input
                                                    className="form-check-input"
                                                    type="radio"
                                                    name="role"
                                                    value="Instructor"
                                                    id="instructor"
                                                    onChange={handleChange}
                                                    checked={formData.role === "Instructor"}
                                                    disabled={!instructorAllowed}
                                                    aria-disabled={!instructorAllowed}
                                                    aria-describedby={!instructorAllowed ? "instructor-hint" : undefined}
                                                />
                                                <label className="form-check-label" htmlFor="instructor">
                                                    Instructor
                                                </label>
                                            </div>
                                        </div>
                                    </div>

                                    {isMaintenance === "true" ? null : (
                                        <div className="mt-3 terms">
                                            <input
                                                style={{ color: "#039606" }}
                                                onChange={() => setIsTermsAccepted(!isTermsAccepted)}
                                                checked={isTermsAccepted}
                                                type="checkbox"
                                                name="terms"
                                                value="accepted"
                                                className="form-check-input pt- me-2"
                                                id="terms-checkbox"
                                                required
                                            />
                                            <label className="form-check-label" htmlFor="terms-checkbox">
                                                I have read and accepted the{" "}
                                                <a
                                                    style={{ textDecoration: "none" }}
                                                    href="/terms-of-service"
                                                    target="_blank"
                                                    rel="noreferrer"
                                                >
                                                    <span style={{ color: "#039606" }}>Terms of Service</span>
                                                </a>
                                            </label>
                                        </div>
                                    )}
                                </>
                            )}

                            {/* navigation buttons */}
                            <div className="d-flex gap-2 mt-4">
                                {step > 0 && (
                                    <button type="button" className="btn secondary-btn w-50 py-2" onClick={goBack}>
                                        Back
                                    </button>
                                )}

                                {step < TOTAL_STEPS - 1 ? (
                                    <button
                                        type="button"
                                        className={`btn primary-btn py-2 ${step > 0 ? "w-50" : "w-100"}`}
                                        onClick={goNext}
                                    >
                                        Next
                                    </button>
                                ) : (
                                    isMaintenance !== "true" && (
                                        <button disabled={!isTermsAccepted} type="submit" className="btn primary-btn w-50 py-2">
                                            Finish
                                        </button>
                                    )
                                )}
                            </div>

                            {step === 0 && (
                                <div className="text-start my-3">
                                    <a href="/login" style={{ color: "#039606", textDecoration: "none" }}>
                                        Already have an account? Click here to login
                                    </a>
                                </div>
                            )}
                        </form>
                    </main>
                )}
            </div>
        </>
    );
}

export default Register;